// Color palette mirrors ColorCodeLightBox.
export const COLOR_CODE_COLORS = [
  "#89D2E8",
  "#FFA4EB",
  "#83E292",
  "#EDB86F",
  "#A5BBFF",
  "#AFCE6C",
  "#F3A4A4",
  "#88DECD",
  "#D2A2FF",
  "#E1D561",
];

export const MAX_COLOR_CODE_COLORS = COLOR_CODE_COLORS.length;

/**
 * The `tagPath` the BE colour pipeline (generateColorBreakdownAggregationPipeline)
 * expects for a filter config's `target`. Single source of truth for BOTH the
 * new filter-UI translator below and the legacy ColorCodeLightBox.
 *
 *   - nestedRollupTagType → target.rollupPath (array, eg ["tags", "subThemes"])
 *   - valuePathType       → target.valuePath  (full leaf path, eg "main.field.value";
 *                           the BE uses it verbatim, same as getValuePathQuery)
 *   - tagType             → target.tagType    (eg "subThemes")
 *
 * Do NOT use the config's top-level `value` for this: ootils sets it to the
 * bare block valuePath ("main.field") for option fields, which is not where
 * the primitive lives.
 */
export const getColorCodeTagPath = (target = {}) => {
  if (target.filterType === "nestedRollupTagType") return target.rollupPath;
  if (target.filterType === "valuePathType") return target.valuePath;
  return target.tagType;
};

/**
 * Convert the new color-code filter-UI state (a single-pick activeFilters array
 * shaped by FilterModule) into the colorbreakDownFilters shape the BE
 * generateColorBreakdownAggregationPipeline expects.
 *
 * Returns undefined when there's nothing picked — caller should treat that as
 * "no color breakdown".
 */
export const translateColorCodeFiltersToBE = (colorCodeFilters) => {
  if (!Array.isArray(colorCodeFilters) || colorCodeFilters.length === 0) return undefined;

  const f = colorCodeFilters[0];
  const t = f?.target || {};
  if (!t.filterType) return undefined;

  // The values carry `badgeColor` assigned by the single-pick wrapper at
  // selection time. The BE color-breakdown pipeline expects the field name
  // `fill`, so we translate badgeColor → fill here. Fallback assigns a color
  // if for some reason badgeColor is missing.
  const values = (f.values || []).slice(0, MAX_COLOR_CODE_COLORS).map((v, i) => ({
    value: v?.value,
    display: v?.display,
    fill: v?.badgeColor || COLOR_CODE_COLORS[i % COLOR_CODE_COLORS.length],
  }));
  if (values.length === 0) return undefined;

  // tagVariant — anno-side vs doc-side. The auto-gen filter configs mark anno
  // filters with `target.isAnno: true`. Doc filters have no isAnno.
  const tagVariant = t.isAnno ? "annoTags" : "tags";

  return {
    filterType: t.filterType,
    tagVariant,
    tagPath: getColorCodeTagPath(t),
    // Split mode's Topics filter: the BE resolves each picked node to its
    // annotation ids on the tenant's tree for this lens (no tagPath).
    ...(t.filterType === "topicClusteringNodeType" ? { topicClusteringLens: t.topicClusteringLens } : {}),
    values,
  };
};
